import asma from '@cannect/assets/img/home/conditions/asma.webp'
import diabetes from '@cannect/assets/img/home/conditions/diabetes.webp'
import disfuncaoEretil from '@cannect/assets/img/home/conditions/disfuncao-eretil.webp'
import dorFibromialgia from '@cannect/assets/img/home/conditions/dor-fibromialgia.webp'
import endometriose from '@cannect/assets/img/home/conditions/endometriose.webp'
import hipertensao from '@cannect/assets/img/home/conditions/hipertensao.webp'
import oncologia from '@cannect/assets/img/home/conditions/oncologia.webp'
import perdaPeso from '@cannect/assets/img/home/conditions/perda-peso.webp'
import saudeMental from '@cannect/assets/img/home/conditions/saude-mental.webp'
import saudeMulher from '@cannect/assets/img/home/conditions/saude-mulher.webp'
import sonoInsonia from '@cannect/assets/img/home/conditions/sono-insonia.webp'

export type HeroConditionFilter = 'pathology' | 'expertise'

/**
 * As doze condições de saúde do hero (Figma node 4333:395), cada uma levando ao agendamento já
 * filtrado.
 *
 * **O filtro vai pelo nome, não pelo id.** `cn_pathologies` e `cn_expertises` são tabelas legadas
 * cujos ids não têm garantia de bater entre ambientes, e as listas que a API devolve são filtradas
 * por disponibilidade — ou seja, mudam sozinhas. Quem resolve nome → id é o `FilterForm`, contra a
 * lista que ele já carrega naquele ambiente. Nome que não resolve abre o agendamento sem filtro,
 * nunca uma lista vazia sem explicação.
 *
 * **Por que uma lista de nomes e não um só:** as listas de dev e de produção divergem bastante
 * (31/07/2026: produção devolve 131 patologias e 19 especialidades; dev devolve 40 e 1). O
 * `FilterForm` usa o primeiro nome que existir, então o mesmo link serve os dois ambientes e
 * sobrevive a uma renomeação sem virar link morto.
 *
 * **Por que quase tudo é `pathology`:** as especialidades disponíveis em produção não incluem
 * Dermatologia, Urologia, Cardiologia, Pneumologia, Endocrinologia nem Oncologia — filtrar por
 * especialidade nessas condições daria zero. As patologias correspondentes existem. A exceção é
 * Saúde da mulher, onde a especialidade (Ginecologia e obstetrícia) é justamente o que existe.
 *
 * **Queda de cabelo saiu da grade** nesta revisão. Além de não constar mais da lista, era o único
 * card sem destino: não há especialidade dermatológica nem patologia equivalente em produção, e
 * ele caía no agendamento sem filtro.
 */
export const HERO_CONDITIONS: {
  label: string
  icon: string
  filterBy: HeroConditionFilter
  filterNames: string[]
  /** `background` é o pastel do hover. `stroke` é o par mais forte da mesma direção cromática,
   * mantido para quando o design pedir contorno ou estado ativo. */
  colors: { background: string; stroke: string }
}[] = [
  {
    label: 'Dor crônica',
    icon: dorFibromialgia,
    filterBy: 'pathology',
    filterNames: ['Dor Crônica'],
    // terracota
    colors: { background: '#F0DDD0', stroke: '#C58E72' }
  },
  {
    label: 'Perda de peso e controle de apetite',
    icon: perdaPeso,
    filterBy: 'pathology',
    filterNames: ['Obesidade', 'Síndrome metabólica'],
    // amarelo aveia
    colors: { background: '#F3E6C9', stroke: '#C6A96E' }
  },
  {
    label: 'Sono e insônia',
    icon: sonoInsonia,
    filterBy: 'pathology',
    filterNames: ['Insônia', 'Distúrbios do sono/ Insônia'],
    // roxo lavanda
    colors: { background: '#DDDCEF', stroke: '#8B88B3' }
  },
  {
    label: 'Estresse, ansiedade e saúde mental',
    icon: saudeMental,
    filterBy: 'pathology',
    filterNames: ['Ansiedade', 'Saúde Mental'],
    // azul lavanda
    colors: { background: '#E2E4F1', stroke: '#9195B8' }
  },
  {
    label: 'Fibromialgia',
    // Reaproveita a ilustração de dor até existir uma própria de fibromialgia.
    icon: dorFibromialgia,
    filterBy: 'pathology',
    filterNames: ['Fibromialgia'],
    // lilás rosado
    colors: { background: '#EADDEC', stroke: '#AA88AF' }
  },
  {
    label: 'Diabetes',
    icon: diabetes,
    filterBy: 'pathology',
    filterNames: ['Diabetes Mellitus'],
    colors: { background: '#DCE9F0', stroke: '#86A8B8' }
  },
  {
    label: 'Endometriose',
    icon: endometriose,
    filterBy: 'pathology',
    filterNames: ['Endometriose'],
    colors: { background: '#F1D9E0', stroke: '#C47F93' }
  },
  {
    label: 'Saúde da mulher',
    icon: saudeMulher,
    filterBy: 'expertise',
    filterNames: ['Ginecologia e obstetrícia', 'Ginecologia'],
    // rosa pêssego
    colors: { background: '#F2DEDA', stroke: '#C48E84' }
  },
  {
    label: 'Disfunção erétil',
    icon: disfuncaoEretil,
    filterBy: 'pathology',
    filterNames: ['Libido'],
    colors: { background: '#D9E4EE', stroke: '#7E9CB3' }
  },
  {
    label: 'Asma e condições pulmonares',
    icon: asma,
    filterBy: 'pathology',
    filterNames: ['Asma', 'Doença de obstrução pulmonar crônica'],
    // azul esverdeado
    colors: { background: '#D8E9E8', stroke: '#74A5A2' }
  },
  {
    label: 'Oncologia',
    icon: oncologia,
    filterBy: 'pathology',
    filterNames: ['Onco/Paliativo', 'Dor Oncológica', 'Cuidado paliativo'],
    // bege dourado
    colors: { background: '#E7E0D1', stroke: '#AA9871' }
  },
  {
    label: 'Hipertensão e saúde cardiovascular',
    icon: hipertensao,
    filterBy: 'pathology',
    filterNames: ['Hipertensão e doenças hipertensivas', 'Doença cardíaca'],
    // vermelho rosado
    colors: { background: '#F1D9D7', stroke: '#C78984' }
  }
]

export const SCHEDULING_PATH = '/agendamento'

export const buildConditionPath = ({
  filterBy,
  filterNames
}: {
  filterBy: HeroConditionFilter
  filterNames: string[]
}) => {
  const params = new URLSearchParams()
  params.set('filter_by', filterBy)
  filterNames.forEach((name) => params.append('filter_by_name', name))

  return `${SCHEDULING_PATH}?${params.toString()}`
}

/**
 * Fundo do hero: três camadas de gradiente sobre `neutral-50`, uma puxando pêssego para baixo e
 * outra puxando verde-claro para cima. Valores literais do arquivo (node 4333:303).
 */
export const HERO_BACKGROUND =
  'linear-gradient(180deg, rgba(228, 170, 119, 0) 34.186%, rgba(228, 170, 119, 0.1) 127.13%), ' +
  'linear-gradient(180deg, rgba(208, 215, 167, 0.1) 0%, rgba(208, 215, 167, 0) 55.194%), ' +
  'linear-gradient(90deg, rgb(250, 250, 250) 0%, rgb(250, 250, 250) 100%)'
